import { useState } from 'react';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';

import { api } from '../../api/context';
import toast from '../../utils/toast';
import utils from '../utils';
import Base from '../Base';
import styles from './Contact.module.scss';

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.ca',
  'ymail.com', 'rocketmail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de',
  'outlook.com', 'outlook.co.uk',
  'live.com', 'msn.com',
  'aol.com',
  'icloud.com', 'me.com', 'mac.com',
  'proton.me', 'protonmail.com', 'protonmail.ch', 'pm.me',
  'gmx.com', 'gmx.us', 'gmx.de',
  'mail.com',
  'yandex.com', 'yandex.ru',
  'zoho.com', 'fastmail.com',
]);

const FREE_EMAIL_MESSAGE =
  'This form is for brands. Please use your company email address.';

const Contact = () => {

  const [ loading, setLoading ] = useState(false);
  const [ done, setDone ] = useState(false);
  const { setDialog } = utils.useDialog();

  return (
    <Base title='Contact Sales'>
      <div className={styles.root}>

        {done ? (
          <div className={styles.done}>
            <p>Thanks — we'll be in touch shortly.</p>
            <button
              type='button'
              onClick={() => setDialog(null)}>
              Close
            </button>
          </div>
        ) : (
          <Formik
            initialValues={{
              name: '',
              brand: '',
              email: '',
              message: '',
            }}
            validationSchema={yup.object({
              name: yup.string()
                .trim()
                .required('Name required'),
              brand: yup.string()
                .trim()
                .required('Brand required'),
              email: yup.string()
                .trim()
                .email('Invalid email address')
                .required('Email required')
                .test(
                  'not-free-email',
                  FREE_EMAIL_MESSAGE,
                  (value) => {
                    if (!value || !value.includes('@')) {
                      return true;
                    }
                    const domain = value.toLowerCase().split('@').pop();
                    return !FREE_EMAIL_DOMAINS.has(domain);
                  },
                ),
              message: yup.string()
                .trim()
                .required('Please tell us what you are looking for'),
            })}
            onSubmit={(values, actions) => {

              if (loading) {
                return;
              }

              setLoading(true);

              api().v1.contact.post(values)
                .then(res => {
                  setLoading(false);

                  if (res.status === 201 || res.status === 200) {
                    actions.resetForm();
                    setDone(true);
                    return;
                  }

                  if (res.status === 400) {
                    if (res.body && res.body.email) {
                      const msg = Array.isArray(res.body.email)
                        ? res.body.email[0]
                        : res.body.email;
                      actions.setFieldError('email', msg);
                    } else {
                      toast.show(toast.KIND.ERROR, res.body);
                    }
                    return;
                  }

                  toast.show(toast.KIND.ERROR, res.body);
                })
                .catch(err => {
                  setLoading(false);
                  toast.show(toast.KIND.WARNING, err);
                });

            }} >

            <Form>

              <div>
                <Field
                  aria-label='name'
                  placeholder='Name'
                  name='name'
                  type='text'
                  autoComplete='name'
                  disabled={loading}
                />
                <ErrorMessage name='name' component='div' />
              </div>

              <div>
                <Field
                  aria-label='brand'
                  placeholder='Brand'
                  name='brand'
                  type='text'
                  autoComplete='organization'
                  disabled={loading}
                />
                <ErrorMessage name='brand' component='div' />
              </div>

              <div>
                <Field
                  aria-label='work email'
                  placeholder='Work Email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  disabled={loading}
                />
                <ErrorMessage name='email' component='div' />
              </div>

              <div>
                <Field
                  aria-label='message'
                  placeholder='Tell us what you are looking for'
                  name='message'
                  as='textarea'
                  rows={5}
                  disabled={loading}
                />
                <ErrorMessage name='message' component='div' />
              </div>

              <button
                aria-label='send'
                type='submit'
                disabled={loading}>

                {loading ? 'Sending…' : 'Send'}

              </button>

            </Form>

          </Formik>
        )}

      </div>
    </Base>
  );
};

export default Contact;
